'use client';

import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Checkbox } from '@/components/ui/checkbox';

export interface ProjectContactDraft {
  id?: string;
  name: string;
  designation: string;
  phone: string;
  email: string;
  phoneOnWhatsApp: boolean;
}

interface ContactsEditorProps {
  contacts: ProjectContactDraft[];
  onChange: (contacts: ProjectContactDraft[]) => void;
  disabled?: boolean;
}

function contactSummary(row: ProjectContactDraft) {
  const parts: string[] = [];

  if (row.name.trim()) {
    parts.push(row.name.trim());
  } else {
    parts.push('Untitled contact');
  }

  if (row.designation.trim()) {
    parts.push(row.designation.trim());
  }

  if (row.phone.trim()) {
    parts.push(row.phone.trim());
  }

  if (row.email.trim()) {
    parts.push(row.email.trim());
  }

  if (row.phoneOnWhatsApp && row.phone.trim()) {
    parts.push('WhatsApp');
  }

  return parts.join(' · ');
}

export function ContactsEditor({ contacts, onChange, disabled }: ContactsEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addContact = () => {
    onChange([
      ...contacts,
      { name: '', designation: '', phone: '', email: '', phoneOnWhatsApp: false },
    ]);
    setEditingIndex(contacts.length);
  };

  const removeContact = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
    setEditingIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      if (current > index) return current - 1;
      return current;
    });
  };

  const updateContact = (
    index: number,
    field: keyof ProjectContactDraft,
    value: string | boolean
  ) => {
    const next = [...contacts];
    const row = { ...next[index], [field]: value };
    if (field === 'phone' && typeof value === 'string' && !value.trim()) {
      row.phoneOnWhatsApp = false;
    }
    next[index] = row;
    onChange(next);
  };

  const moveContact = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === contacts.length - 1) return;

    const next = [...contacts];
    const target = direction === 'up' ? index - 1 : index + 1;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
    setEditingIndex((current) => {
      if (current === null) return null;
      if (current === index) return target;
      if (current === target) return index;
      return current;
    });
  };

  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold">Contact details</Label>
      <p className="text-sm text-muted-foreground">
        For each contact, provide at least a phone number or an email. You can mark if the phone
        number is reachable on WhatsApp.
      </p>

      {contacts.length === 0 ? (
        <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground space-y-4">
          <div>
            <p>No contacts added yet.</p>
            <p className="text-sm">Optional — add people to reach about this project.</p>
          </div>
          <Button type="button" variant="outline" onClick={addContact} disabled={disabled}>
            <Plus className="h-4 w-4 mr-1" />
            Add contact
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((row, index) => {
            const isEditing = editingIndex === index;

            if (!isEditing) {
              return (
                <div
                  key={row.id || `new-${index}`}
                  className="flex items-center gap-3 border rounded-lg p-4 bg-muted/30"
                >
                  <p className="flex-1 text-sm font-medium">{contactSummary(row)}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingIndex(index)}
                    disabled={disabled}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                </div>
              );
            }

            return (
              <div
                key={row.id || `new-${index}`}
                className="border rounded-lg p-4 bg-card space-y-3"
              >
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-muted-foreground shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveContact(index, 'up')}
                      disabled={disabled || index === 0}
                      aria-label="Move up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveContact(index, 'down')}
                      disabled={disabled || index === contacts.length - 1}
                      aria-label="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground flex-1">Contact</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setEditingIndex(null)}
                    disabled={disabled}
                    className="h-8 w-8 shrink-0 text-primary"
                    aria-label="Done editing contact"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    onClick={() => removeContact(index)}
                    disabled={disabled}
                    aria-label="Remove contact"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor={`contact-name-${index}`}>Name (optional)</Label>
                    <Input
                      id={`contact-name-${index}`}
                      value={row.name}
                      onChange={(e) => updateContact(index, 'name', e.target.value)}
                      placeholder="Contact name"
                      disabled={disabled}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor={`contact-designation-${index}`}>Designation (optional)</Label>
                    <Input
                      id={`contact-designation-${index}`}
                      value={row.designation}
                      onChange={(e) => updateContact(index, 'designation', e.target.value)}
                      placeholder="e.g., Project lead, Coordinator"
                      disabled={disabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label id={`contact-phone-label-${index}`}>Phone (optional)</Label>
                    <PhoneInput
                      aria-labelledby={`contact-phone-label-${index}`}
                      placeholder="Enter phone number"
                      value={row.phone || undefined}
                      onChange={(v) => updateContact(index, 'phone', v ?? '')}
                      disabled={disabled}
                      defaultCountry="IN"
                    />
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id={`contact-wa-${index}`}
                        checked={row.phoneOnWhatsApp && Boolean(row.phone.trim())}
                        disabled={disabled || !row.phone.trim()}
                        onCheckedChange={(checked) =>
                          updateContact(index, 'phoneOnWhatsApp', checked === true)
                        }
                      />
                      <Label
                        htmlFor={`contact-wa-${index}`}
                        className="text-sm font-normal cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        This number is available on WhatsApp
                      </Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`contact-email-${index}`}>Email (optional)</Label>
                    <Input
                      id={`contact-email-${index}`}
                      value={row.email}
                      onChange={(e) => updateContact(index, 'email', e.target.value)}
                      placeholder="name@example.com"
                      type="email"
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            onClick={addContact}
            disabled={disabled}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add more contact
          </Button>
        </div>
      )}
    </div>
  );
}
